/**
 * Material Slot Pattern Config
 * 
 * Defines a material slot detected from a 3D model and its customization options.
 * Each slot represents a unique material in the model that can be customized.
 */

import * as THREE from 'three';
import type { MaterialProps } from '../types';

export interface MaterialSlotOption {
  id: string;
  name: string;
  materialProps?: MaterialProps;
  textureUrls?: {
    map?: string;
    normalMap?: string;
    roughnessMap?: string;
    metalnessMap?: string;
    aoMap?: string;
    emissiveMap?: string;
  };
}

export class MaterialSlotPatternConfig {
  /** Unique identifier for this slot (auto-generated from material name) */
  public readonly id: string;
  
  /** Display name for UI */
  public readonly name: string;
  
  /** Original material from the model */
  public readonly originalMaterial: THREE.Material;
  
  /** Material index (for multi-material meshes) */
  public readonly materialIndex?: number;
  
  /** Mesh names that use this material */
  public readonly meshNames: string[] = [];
  
  /** Parent object name (the node/object this material belongs to) */
  public objectName: string = '';
  
  /** Available options for this slot */
  public options: MaterialSlotOption[] = [];
  
  /** Currently selected option ID */
  public selectedOptionId: string | null = null;
  
  constructor(params: {
    id: string;
    name: string;
    originalMaterial: THREE.Material;
    materialIndex?: number;
    objectName?: string;
  }) {
    this.id = params.id;
    this.name = params.name;
    this.originalMaterial = params.originalMaterial;
    this.materialIndex = params.materialIndex;
    this.objectName = params.objectName ?? '';
    
    // Create default "Original" option from the material
    this.addDefaultOption();
  }
  
  /**
   * Add a mesh name to this slot
   */
  public addMeshName(meshName: string): void {
    if (!this.meshNames.includes(meshName)) {
      this.meshNames.push(meshName);
    }
  }
  
  /**
   * Add a customization option
   */
  public addOption(option: MaterialSlotOption): void {
    this.options.push(option);
  }
  
  /**
   * Create default option from original material
   */
  private addDefaultOption(): void {
    const mat = this.originalMaterial as THREE.MeshStandardMaterial;
    
    const defaultOption: MaterialSlotOption = {
      id: 'original',
      name: 'Original',
      materialProps: {
        color: mat.color ? `#${mat.color.getHexString()}` : undefined,
        roughness: mat.roughness,
        metalness: mat.metalness,
        emissive: mat.emissive ? `#${mat.emissive.getHexString()}` : undefined,
        emissiveIntensity: mat.emissiveIntensity,
      },
    };
    
    this.options.push(defaultOption);
    this.selectedOptionId = 'original';
  }
  
  /**
   * Get the currently selected option
   */
  public getSelectedOption(): MaterialSlotOption | null {
    return this.options.find(opt => opt.id === this.selectedOptionId) ?? null;
  }
  
  /**
   * Select an option by ID
   */
  public selectOption(optionId: string): boolean {
    const option = this.options.find(opt => opt.id === optionId);
    if (option) {
      this.selectedOptionId = optionId;
      return true;
    }
    return false;
  }
}
